import { useRef } from 'react';

import type { ResponseError } from '@rtkwlf/asset-data-service';
import { captureException } from '@sentry/react';
import hasIn from 'lodash/hasIn';
import isObject from 'lodash/isObject';
import { useTranslation } from 'react-i18next';

import { useAssetsDevices } from '@apis/asset-data-service/queries/use-assets-devices/use-assets-devices';
import { ActionDrawerProvider } from '@packages/components/action-drawer/action-drawer-context';
import { Stack } from '@packages/fenrir/components/stack';
import { TOAST_DURATION } from 'ae-constants/toast';

import type { AssetsSnackbarRef } from '../../components/assets-snackbar/assets-snackbar';
import { AssetsTable } from '../../components/assets-table/assets-table';
import { useAssetTableConfig } from '../../components/assets-table/use-asset-table-config/use-asset-table-config';

export const DeviceListPage = () => {
  const snackbarRef = useRef<AssetsSnackbarRef>(null);
  const {
    initialState,
    apiRef,
    sortModel,
    onSortModelChange,
    setFilterModel,
    applyFilters,
    assetListParams,
    paginationModel,
    setPaginationModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columns,
  } = useAssetTableConfig(snackbarRef);

  const { t } = useTranslation('ui');
  const { data, isFetching, isError, error } = useAssetsDevices(
    assetListParams,
    {
      meta: {
        // TODO: Use snackbar once snackbar context and meta is added
        onErrorToastData: async (error) => {
          if (!isObject(error) || !hasIn(error, 'response.status')) return;

          const { response } = error as ResponseError;

          if (response.status === 400) {
            const responseBody = await response.json();

            // Capture Sentry exception for 400 errors so we can add validation for them
            captureException(responseBody, {
              tags: { feature: 'assets', query: 'queryDevices' },
            });

            return {
              appearance: 'danger',
              duration: TOAST_DURATION,
              title: t('error'),
              message: responseBody.message,
            };
          }
        },
      },
    },
  );

  return (
    <Stack sx={{ width: '100%' }} spacing={2}>
      <ActionDrawerProvider>
        <AssetsTable
          assetTableConfig={{
            initialState,
            apiRef,
            sortModel,
            onSortModelChange,
            setFilterModel,
            applyFilters,
            assetListParams,
            paginationModel,
            setPaginationModel,
            columnVisibilityModel,
            setColumnVisibilityModel,
            columns,
          }}
          rows={Array.from(data?.devices || [])}
          total={data?.metadata.total || 0}
          isLoading={isFetching}
          isError={isError}
          error={error}
          snackbarRef={snackbarRef}
        />
      </ActionDrawerProvider>
    </Stack>
  );
};

export default DeviceListPage;

export const Component = () => <DeviceListPage />;
